import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { LogOut, UserPlus } from "lucide-react";

import { hostelService, type Allocation, type VisitorLog } from "../../services/hostelService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { errorLine } from "../inventory/InventoryParts";
import { SearchPick } from "../library/Pickers";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { VISITOR_TYPES, clock, day, isInside, labelFrom, visitorConfig } from "./hostelLogic";

function SignInForm({ boarders, onClose, onDone }: { boarders: Allocation[]; onClose: () => void; onDone: (row: VisitorLog) => void }) {
  const [boarderId, setBoarderId] = useState("");
  const boarder = boarders.find((row) => String(row.id) === boarderId);
  const save = useMutation({ mutationFn: hostelService.logVisitor, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!boarder) return;
    const data = new FormData(event.currentTarget);
    const text = (name: string) => String(data.get(name) ?? "").trim() || null;
    save.mutate({
      hostel_id: boarder.hostel,
      student_id: boarder.student,
      visitor_name: text("visitor_name"),
      visitor_type: text("visitor_type"),
      visitor_relationship: text("relationship"),
      visitor_phone: text("phone"),
      visitor_id_number: text("id_number"),
      purpose: text("purpose"),
      items_brought: text("items"),
    });
  };
  return (
    <FormModal
      id="visitor"
      title="Sign a visitor in"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The visitor could not be signed in.") : undefined}
      saveLabel="Sign in"
      note="Recorded as arriving now."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Who they are visiting" divided={false}>
        <Field label="Boarder" as="div" className="sm:col-span-2">
          <SearchPick
            items={boarders}
            value={boarderId}
            onChange={setBoarderId}
            label={(row) => row.student_name}
            detail={(row) => `${row.hostel_name} · room ${row.room_number}`}
            text={(row) => `${row.student_name} ${row.student_id}`}
            placeholder="Search by boarder's name"
            empty="No boarder matches"
            required
          />
        </Field>
      </FormSection>
      <FormSection title="The visitor">
        <Field label="Name">
          <input required name="visitor_name" maxLength={100} className="input mt-1" />
        </Field>
        <Field label="Who they are">
          <select name="visitor_type" defaultValue="parent" className="input mt-1">
            {VISITOR_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Relationship" optional hint="e.g. mother, uncle.">
          <input name="relationship" maxLength={50} className="input mt-1" />
        </Field>
        <Field label="Phone" optional>
          <input type="tel" name="phone" maxLength={20} className="input mt-1" />
        </Field>
        <Field label="ID number" optional hint="National ID or passport, if checked.">
          <input name="id_number" maxLength={50} className="input mt-1" />
        </Field>
        <Field label="Why they came">
          <input required name="purpose" className="input mt-1" placeholder="e.g. Visiting day; brought shopping." />
        </Field>
        <Field label="Brought in" optional className="sm:col-span-2" hint="Anything handed over, for the record.">
          <input name="items" className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Who came to see a boarder, and whether they have left. */
export function VisitorsPanel({ visitors, boarders, isLoading, isError, onRetry, onChanged }: {
  visitors?: VisitorLog[];
  boarders: Allocation[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const signOut = useMutation({
    mutationFn: hostelService.signOutVisitor,
    onSuccess: (row) => {
      setViewing(null);
      setNotice({ tone: "success", text: `${row.visitor_name} signed out at ${clock(row.visit_time_out)}.` });
      onChanged();
    },
  });
  const active = boarders.filter((row) => row.status === "active").sort((a, b) => a.student_name.localeCompare(b.student_name));

  const columns: RegisterColumn<VisitorLog>[] = [
    { header: "Date", cell: (row) => (row.visit_date === today ? "Today" : day(row.visit_date)), exportValue: (row) => day(row.visit_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "In", cell: (row) => clock(row.visit_time_in), exportValue: (row) => clock(row.visit_time_in), className: "tabular-nums text-gray-700" },
    { header: "Out", cell: (row) => clock(row.visit_time_out) || <span className="text-gray-300">—</span>, exportValue: (row) => clock(row.visit_time_out), className: "tabular-nums text-gray-700" },
    { header: "Visitor", cell: (row) => <span className="font-medium text-gray-900">{row.visitor_name}</span>, exportValue: (row) => row.visitor_name },
    { header: "Who", cell: (row) => row.visitor_relationship || labelFrom(VISITOR_TYPES, row.visitor_type), exportValue: (row) => row.visitor_relationship || labelFrom(VISITOR_TYPES, row.visitor_type), className: "text-gray-700" },
    { header: "Visiting", cell: (row) => row.student_name, exportValue: (row) => row.student_name, className: "text-gray-700" },
    { header: "Why", cell: (row) => row.purpose, exportValue: (row) => row.purpose, className: "min-w-[12rem] max-w-sm whitespace-normal text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot label={row.visit_time_out ? "Left" : isInside(row, today) ? "Inside" : "Not signed out"} dot={row.visit_time_out ? "bg-gray-400" : isInside(row, today) ? "bg-emerald-500" : "bg-amber-500"} />, exportValue: (row) => (row.visit_time_out ? "Left" : "Inside") },
  ];

  const visit = viewing !== null ? visitors?.find((row) => row.id === viewing) : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={visitors ? "loaded" : "loading"}
        title="Visitors"
        description="Sign visitors in at the gate and out when they leave."
        actions={
          <button type="button" disabled={!active.length} onClick={() => setAdding(true)} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <UserPlus className="mr-1.5 h-3.5 w-3.5" /> Sign a visitor in
          </button>
        }
        rows={visitors}
        config={visitorConfig(today)}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          signOut.reset();
          setViewing(row.id);
        }}
        openLabel={(row) => (row.visit_time_out ? "Open" : "Sign out")}
        noun={["visit", "visits"]}
        exportName={{ file: "boarding-visitors", title: "Boarding Visitors" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No visitors recorded."
        initialState={(visitors ?? []).some((row) => row.visit_date === today) ? { view: "today" } : { view: "all" }}
      />

      {visit && (
        <DetailsModal
          title={`${visit.visitor_name} · visiting ${visit.student_name}`}
          headline={{ label: visit.visit_time_out ? "Visit" : "In since", value: visit.visit_time_out ? `${clock(visit.visit_time_in)} – ${clock(visit.visit_time_out)}` : clock(visit.visit_time_in), note: `${day(visit.visit_date)} · ${visit.hostel_name}` }}
          status={{ label: visit.visit_time_out ? "Left" : "Inside", dot: visit.visit_time_out ? "bg-gray-400" : "bg-emerald-500" }}
          facts={[
            ["Who", [labelFrom(VISITOR_TYPES, visit.visitor_type), visit.visitor_relationship].filter(Boolean).join(", ")],
            ["Phone", visit.visitor_phone ?? undefined],
            ["ID number", visit.visitor_id_number ?? undefined],
            ["Signed in by", visit.logged_by_name],
            ["Signed out by", visit.approved_by_name],
          ]}
          sections={[
            { title: "Why they came", body: visit.purpose },
            { title: "Brought in", body: visit.items_brought },
            { title: "Notes", body: visit.notes },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {!visit.visit_time_out && (
                <button type="button" disabled={signOut.isPending} onClick={() => signOut.mutate({ id: visit.id })} className="btn btn-primary gap-1.5">
                  <LogOut className="h-4 w-4" /> Signed out now
                </button>
              )}
            </>
          }
        >
          {signOut.isError && errorLine(signOut.error, "The visitor could not be signed out.")}
        </DetailsModal>
      )}

      {adding && (
        <SignInForm
          boarders={active}
          onClose={() => setAdding(false)}
          onDone={(saved) => {
            setAdding(false);
            setNotice({ tone: "success", text: `${saved.visitor_name} signed in to see ${saved.student_name}.` });
            onChanged();
          }}
        />
      )}
    </div>
  );
}
